/**
 * Loading skeleton for BrandedEmbeddablePlaylist.
 *
 * Rendered inside <BrandedShell> while the first playlist fetch is in flight.
 * Mirrors the real shell layout (player frame + playlist tray) so the page
 * doesn't jump when real content swaps in. Intentionally minimal — a single
 * pulse animation on dimmed blocks; no fake text, no fake thumbnails.
 *
 * Before this existed, the empty-but-mounted state rendered placeholder text
 * like `'s highlight` and `0 views` because the ViewModel's atoms had been
 * initialized but no clip data was present yet. The skeleton replaces that
 * with an explicitly "loading" surface.
 */
import styled, { keyframes } from "styled-components";
import { Box, Flex } from "rebass/styled-components";

const pulse = keyframes`
  0%, 100% { opacity: 0.35; }
  50%      { opacity: 0.6; }
`;

const SkeletonShell = styled(Flex)`
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const SkeletonPlayer = styled(Box)`
  /* Matches PlayerRow's 55% min — keeps the skeleton's player surface the
     same height as the real one so there's no layout shift on swap. */
  flex: 1 1 auto;
  min-height: 55%;
  background: linear-gradient(180deg, #131319 0%, #0c0c11 100%);
  border-radius: 6px;
  margin: 8px 8px 0;
  animation: ${pulse} 1.4s ease-in-out infinite;
`;

const SkeletonTray = styled(Flex)`
  /* Matches PlaylistColumn's 45% ceiling. */
  flex: 0 1 auto;
  flex-direction: column;
  gap: 6px;
  max-height: 45%;
  min-height: 0;
  padding: 12px 14px;
`;

const SkeletonRow = styled(Box)<{ $width?: string; $delay?: string }>`
  background: rgba(255, 255, 255, 0.04);
  border-radius: 4px;
  height: 36px;
  width: ${({ $width }) => $width ?? "100%"};
  animation: ${pulse} 1.4s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay ?? "0s"};
`;

export const PlaylistSkeleton = () => (
  <SkeletonShell>
    <SkeletonPlayer />
    <SkeletonTray>
      <SkeletonRow $delay="0s" />
      <SkeletonRow $delay="0.15s" $width="92%" />
      <SkeletonRow $delay="0.3s" $width="96%" />
      <SkeletonRow $delay="0.45s" $width="88%" />
    </SkeletonTray>
  </SkeletonShell>
);
